import React from 'react';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Select, SelectOption } from 'components/UI/V3/Forms/Select/Select';
import styles from './EvaluationPricing.module.scss';
import PriceField from 'components/UI/V3/Forms/PriceField/PriceField';
import { ServiceDuration } from 'model/Service';
import { FieldError } from '@spike/model';
import { getErrorMessage } from './Validations';

interface EvaluationPricingProps {
    price: number;
    duration: ServiceDuration;
    errors: Array<FieldError>;
    onUpdatePrice: (price: number) => void;
    onUpdateDuration: (duration: ServiceDuration) => void;
}

// Duration options: 15, 30, 45, 60 minutes
const durationOptions: SelectOption<number>[] = [
    { value: '15', label: '15 minutes', data: 15 },
    { value: '30', label: '30 minutes', data: 30 },
    { value: '45', label: '45 minutes', data: 45 },
    { value: '60', label: '1 hour', data: 60 }
];

export const EvaluationPricing: React.FC<EvaluationPricingProps> = ({
    price,
    duration,
    errors,
    onUpdatePrice,
    onUpdateDuration
}) => {
    // Convert ServiceDuration to minutes for the select
    const convertDurationToMinutes = (serviceDuration: ServiceDuration): string => {
        const simple = serviceDuration.simple;
        if (!simple) return '15'; // Default to 15 minutes

        const totalMinutes = (simple.hours || 0) * 60 + (simple.minutes || 0);

        // Return the closest valid option or default to 15
        if (totalMinutes >= 60) return '60';
        if (totalMinutes >= 45) return '45';
        if (totalMinutes >= 30) return '30';
        return '15';
    };

    const handleDurationChange = (value: string, option: SelectOption<number>) => {
        const minutes = parseInt(value);

        let hours = 0;
        let mins = 0;

        if (minutes >= 60) {
            // 1 hour case
            hours = 1;
            mins = 0;
        } else {
            // Minutes case (15, 30, 45)
            hours = 0;
            mins = minutes;
        }

        const newDuration: ServiceDuration = {
            simple: {
                hours,
                minutes: mins
            },
            variable: duration.variable || []
        };

        onUpdateDuration(newDuration);
    };

    return (
        <div className={styles.section}>
            <div className={styles.priceRow}>
                <div className={styles.priceField}>
                    <PriceField
                        label="Price"
                        value={price}
                        onChange={value => onUpdatePrice(value || 0)}
                        placeholder="$0.00"
                        scale={2}
                        precision={6}
                    />
                </div>
                <div className={styles.durationField}>
                    <Select
                        label="Duration"
                        required
                        value={convertDurationToMinutes(duration)}
                        error={getErrorMessage(errors, 'duration')}
                        onChange={handleDurationChange}
                        options={durationOptions}
                        placeholder="Select"
                    />
                </div>
            </div>
        </div>
    );
};

export default EvaluationPricing;
